import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ShelterIndexPropTypes } from 'dpl/shared/utils/customPropTypes';
import { LEARNING_CENTER_SHELTER_PATH } from 'dpl/shared/constants/urls';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import SmartImage from 'dpl/components/SmartImage';
import ExpandedPaginator from 'dpl/components/ExpandedPaginator';
import ResultsListItem from 'dpl/shelter_search_results/components/ResultsListItem';
import EmptyResults from 'dpl/shelter_search_results/components/EmptyResults';

import documentJpg from 'dpl/assets/document.jpg';

export function ResultsListHelpLink() {
  return (
    <a
      className="ph4 pv4 mt2 ba b--light-gray br1 db flex items-center"
      href={LEARNING_CENTER_SHELTER_PATH}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div>
        <SmartImage
          loadingClass="bg-light-gray"
          src={documentJpg}
          width="30px"
        />
      </div>
      <p className="ml4">How the adoption process works</p>
    </a>
  );
}

export default function ResultsList({
  currentPage,
  itemsPerPage,
  mapIsLoaded,
  onShelterClick,
  onShelterClose,
  selectedShelterId,
  setPage,
  sheltersData
}) {
  const {
    meta: { isSuccess },
    value: {
      meta: { total_item_count: totalShelters },
      data: shelters
    }
  } = sheltersData;

  const helpLinkIdx = Math.min(shelters.length, 7) - 1;

  return (
    <div className="ShelterResultsList">
      <LoadingWrapper isLoading={!isSuccess}>
        {totalShelters < 1 && <EmptyResults />}
        {totalShelters > 0 && (
          <Fragment>
            <p className={classnames('mb3 f2', { 'dn-l': !mapIsLoaded })}>
              <span className="fw-medium">
                {totalShelters === 1
                  ? `${totalShelters} result`
                  : `${totalShelters} results`}
              </span>{' '}
              for shelters & rescues near you
            </p>
            <ul className="mb6">
              {shelters.map((shelter, idx) => {
                const { id: shelterId } = shelter;
                const isSelected = selectedShelterId === shelter.id;

                return (
                  <Fragment key={shelterId}>
                    {idx === helpLinkIdx && (
                      <li
                        className={classnames('transition', {
                          'o-40': Boolean(selectedShelterId)
                        })}
                      >
                        <ResultsListHelpLink />
                      </li>
                    )}
                    <li
                      className={classnames('b--light-gray transition', {
                        bb: idx !== helpLinkIdx - 1,
                        bt: idx === 0,
                        'o-40': selectedShelterId && !isSelected
                      })}
                    >
                      <ResultsListItem
                        shelter={shelter}
                        onClick={e => {
                          e.stopPropagation();
                          onShelterClick(shelter.id);
                        }}
                      />
                    </li>
                  </Fragment>
                );
              })}
            </ul>
          </Fragment>
        )}
        {totalShelters > itemsPerPage && (
          <div className="tc">
            <ExpandedPaginator
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageClick={pageNumber => {
                onShelterClose && onShelterClose();
                setPage(pageNumber);
              }}
              shownPageNumberPadding={2}
              totalItemCount={totalShelters}
            />
          </div>
        )}
      </LoadingWrapper>
    </div>
  );
}

ResultsList.propTypes = {
  currentPage: PropTypes.number.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  mapIsLoaded: PropTypes.bool.isRequired,
  onShelterClick: PropTypes.func.isRequired,
  onShelterClose: PropTypes.func,
  selectedShelterId: PropTypes.number,
  setPage: PropTypes.func.isRequired,
  sheltersData: PropTypes.shape({
    meta: PropTypes.shape({
      isSuccess: PropTypes.bool
    }),
    value: PropTypes.shape({
      data: PropTypes.arrayOf(PropTypes.shape(ShelterIndexPropTypes))
        .isRequired,
      meta: PropTypes.shape({
        total_item_count: PropTypes.number
      })
    })
  }).isRequired
};

ResultsList.defaultProps = {
  onShelterClose: null,
  selectedShelterId: null
};
